import type { CompiledForm, CompiledNode, ExtraRowCells, FieldValue, MatrixAnswers } from '../model/types';
import { withExtraRowRemoved } from './answers';
import { sameAnswer } from './field-walk';
import { extraCellNodeId } from './model-row';

// The historical cells `current` changed against `opened`, compared the way the acceptance compares
// them: a cell that goes from empty to empty is no edit, and the rows past the history are new.
export function editedHistoricalCells(
  opened: MatrixAnswers,
  current: MatrixAnswers,
): Record<string, ExtraRowCells<FieldValue>> {
  const tables: Record<string, ExtraRowCells<FieldValue>> = {};
  for (const [tableId, entry] of Object.entries(current.tables)) {
    const historical = opened.tables[tableId]?.extraRows ?? [];
    const edited: ExtraRowCells<FieldValue> = {};
    historical.forEach((row, rowIndex) => {
      for (const [columnKey, value] of Object.entries(entry.extraRows[rowIndex] ?? {})) {
        const column = Number(columnKey);
        if (!sameAnswer(row[column], value)) edited[rowIndex] = { ...edited[rowIndex], [column]: value };
      }
    });
    if (Object.keys(edited).length > 0) tables[tableId] = edited;
  }
  return tables;
}

// The node ids compile gives those cells, the list validatedFieldIds takes.
export function editedCellNodeIds(opened: MatrixAnswers, current: MatrixAnswers): string[] {
  return Object.entries(editedHistoricalCells(opened, current)).flatMap(([tableId, rows]) =>
    Object.entries(rows).flatMap(([rowKey, cells]) =>
      Object.keys(cells).map((columnKey) => extraCellNodeId(tableId, Number(rowKey), Number(columnKey))),
    ),
  );
}

// Tables are not in `fields` and may sit inside another table's cell, so the count walks the tree.
export function historicalRowCounts(form: CompiledForm): Record<string, number> {
  const counts: Record<string, number> = {};

  const walk = (node: CompiledNode): void => {
    if (node.kind !== 'table') return;
    for (const row of node.cells) {
      for (const cell of row) {
        if (cell.node) walk(cell.node);
      }
    }
    const historical = node.extraRows.filter((row) => row.historical).length;
    if (historical > 0) counts[node.id] = historical;
  };

  form.nodes.forEach(walk);
  return counts;
}

export function newRowsStart(form: CompiledForm, tableId: string): number {
  return historicalRowCounts(form)[tableId] ?? 0;
}

// A saved row never leaves, not even in a correction. Removing one by index would also make the
// body drop a new row in its place, since buildAnswersSnapshot slices by the saved count.
export function withNewExtraRowRemoved(
  form: CompiledForm,
  answers: MatrixAnswers,
  tableId: string,
  rowIndex: number,
): MatrixAnswers {
  if (rowIndex < newRowsStart(form, tableId)) return answers;
  return withExtraRowRemoved(answers, tableId, rowIndex);
}
